import type { ResponseProps } from './api.types';

export interface ApiErrorResponse {
  status?: number;
  text?: string;
  body?: unknown;
  headers?: Record<string, unknown>;
  url?: string;
  method?: string;
}

const asString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

const asHeaders = (value: unknown): Record<string, unknown> | undefined => {
  if (!value || typeof value !== 'object') {
    return undefined;
  }
  const { entries } = value as { entries?: unknown };
  return typeof entries === 'function'
    ? Object.fromEntries(entries.call(value) as Iterable<[string, unknown]>)
    : { ...(value as Record<string, unknown>) };
};

/*
  A superagent node Response reaches back into the http.ClientRequest and from
  there into the socket, which points at the request again. Holding that graph
  on the error makes the error impossible to JSON.stringify, and Jest sends
  `failureDetails` over worker IPC raw — one failing test that surfaced an API
  error took its whole worker down with "Converting circular structure to
  JSON". Flattening to the fields consumers actually read keeps the error
  serialisable and lets the socket graph be collected.
*/
const toSerializableResponse = (
  response: unknown
): ApiErrorResponse | undefined => {
  if (!response || typeof response !== 'object') {
    return undefined;
  }

  const source = response as Record<string, unknown>;
  const request = source.req as Record<string, unknown> | undefined;

  return {
    status: typeof source.status === 'number' ? source.status : undefined,
    text: asString(source.text),
    body: source.body,
    headers: asHeaders(source.headers),
    url: asString(source.url) ?? asString(request?.url),
    method: asString(source.method) ?? asString(request?.method),
  };
};

export class ApiError extends Error {
  private apiError: {
    body?: ResponseProps['body'];
    endpoint: ResponseProps['endpoint'];
    errors?: unknown;
    headers: ResponseProps['headers'];
    method: ResponseProps['method'];
    status: ResponseProps['status'];
    statusCode: ResponseProps['status'];
    response?: ApiErrorResponse;
  };

  constructor({
    endpoint,
    method,
    status,
    body,
    response,
    headers,
  }: ResponseProps) {
    super(`${status} - Api Error`);

    const errors =
      typeof body === 'object' ? body.errors || body.error || body : body;

    this.apiError = {
      endpoint,
      method,
      status,
      statusCode: status,
      response: toSerializableResponse(response),
      errors,
      headers,
    };
  }

  getStatusCode() {
    return this.apiError.statusCode;
  }

  getEndpoint() {
    return this.apiError.endpoint;
  }

  getErrors() {
    return this.apiError.errors;
  }

  getMethod() {
    return this.apiError.method;
  }

  getHeaders() {
    return this.apiError.headers;
  }

  getResponse() {
    return this.apiError.response;
  }
}
